// ============================================================================
// RoofPolygonLayoutSvg.tsx — Modulbelegung auf der ECHTEN Dachform (SVG)
// ----------------------------------------------------------------------------
// Rein darstellende SVG-Visualisierung der Belegung: zeichnet den realen
// Dachumriss (beliebiges Polygon, z. B. L-Form), die platzierten Module und die
// Störflächen (Hindernisse) in EINEM gemeinsamen lokalen Meter-Frame. Keine
// Karte/WebGL — deterministisch, screenshot-fähig (auch fürs Konzept-PDF).
//
// Geometrie/Belegung kommen aus roof-belegung.ts (→ roof-core). Hier nur Frame,
// Skalierung (Bounding-Box → viewBox) und Y-Flip (Geo-Nord nach oben).
// ============================================================================
import type { LocalPoint, ModuleRectLocal } from "@/lib/roof-belegung.js";

interface RoofPolygonLayoutSvgProps {
  /** Dachumriss als lokales Meter-Polygon. */
  polygon: LocalPoint[];
  /** platzierte Module als lokale Meter-Polygone. */
  modules: ModuleRectLocal[];
  /** Störflächen/Hindernisse als lokale Meter-Polygone. */
  obstacles?: LocalPoint[][];
  /** Sperrflächen/No-Go-Zonen als lokale Meter-Polygone. */
  keepouts?: LocalPoint[][];
  /** maximale Render-Breite in px. */
  maxWidthPx?: number;
  /** IDs manuell entfernter Module (ausgegraut, klickbar zum Wiederherstellen). */
  disabledIds?: ReadonlySet<string>;
  /** Klick auf ein Modul → ID umschalten (manuelles Löschen/Wiederherstellen). */
  onToggleModule?: (id: string) => void;
}

function pts(points: LocalPoint[]): string {
  return points.map((p) => `${p.x},${p.y}`).join(" ");
}

/**
 * Zeichnet die Belegung auf der realen Dachform. Alle Eingaben sind bereits in
 * EINEM lokalen Meter-Frame (gemeinsamer Ursprung) — diese Komponente bestimmt
 * nur die Bounding-Box, spiegelt Y (damit Nord oben liegt) und skaliert auf
 * maxWidthPx. role=img + aria-label nennt die Modulanzahl.
 */
export function RoofPolygonLayoutSvg({
  polygon,
  modules,
  obstacles,
  keepouts,
  maxWidthPx = 460,
  disabledIds,
  onToggleModule,
}: RoofPolygonLayoutSvgProps) {
  if (!polygon || polygon.length < 3) return null;

  // Bounding-Box über ALLE Geometrien (Dach umschließt i. d. R. den Rest).
  const all: LocalPoint[] = [
    ...polygon,
    ...modules.flatMap((m) => m.corners),
    ...(obstacles ?? []).flat(),
    ...(keepouts ?? []).flat(),
  ];
  const xs = all.map((p) => p.x);
  const ys = all.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  const widthM = maxX - minX;
  const heightM = maxY - minY;
  if (!(widthM > 0) || !(heightM > 0)) return null;

  // Y-Flip: Geo-Nord (größeres y) soll oben liegen. Wir transformieren jeden
  // Punkt nach (x - minX, maxY - y) → Ursprung links-oben, Meter beibehalten.
  const tx = (p: LocalPoint): LocalPoint => ({ x: p.x - minX, y: maxY - p.y });

  const pad = Math.max(widthM, heightM) * 0.03;
  const vbW = widthM + pad * 2;
  const vbH = heightM + pad * 2;

  const scale = maxWidthPx / vbW;
  const widthPx = maxWidthPx;
  const heightPx = Math.max(40, vbH * scale);
  const stroke = Math.max(widthM * 0.0025, 0.012);

  const polygonT = polygon.map(tx);
  const modulesT = modules.map((m) => ({ id: m.id, corners: m.corners.map(tx) }));
  const interactive = typeof onToggleModule === "function";
  const enabledCount = modules.filter((m) => !disabledIds?.has(m.id)).length;
  const obstaclesT = (obstacles ?? []).filter((o) => o.length >= 3).map((o) => o.map(tx));
  const keepoutsT = (keepouts ?? []).filter((k) => k.length >= 3).map((k) => k.map(tx));

  return (
    <svg
      viewBox={`${-pad} ${-pad} ${vbW} ${vbH}`}
      width={widthPx}
      height={heightPx}
      role="img"
      aria-label={`Modulbelegung auf realer Dachform: ${enabledCount} Module${
        interactive ? " (Modul anklicken zum Entfernen/Wiederherstellen)" : ""
      }`}
      style={{ display: "block", maxWidth: "100%", height: "auto", borderRadius: 6 }}
    >
      {/* Schraffur-Muster für Sperrflächen */}
      <defs>
        <pattern id="keepout-hatch" patternUnits="userSpaceOnUse" width={stroke * 8} height={stroke * 8} patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2={stroke * 8} stroke="#b45309" strokeWidth={stroke * 1.5} />
        </pattern>
      </defs>

      {/* Dachumriss (echte Form) */}
      <polygon
        points={pts(polygonT)}
        fill="var(--color-bg-subtle, #f1f5f9)"
        stroke="var(--color-border, #94a3b8)"
        strokeWidth={stroke * 2}
        strokeLinejoin="round"
      />

      {/* Module — manuell entfernte ausgegraut/gestrichelt; klickbar zum Umschalten */}
      {modulesT.map(({ id, corners }, i) => {
        const off = disabledIds?.has(id) ?? false;
        return (
          <polygon
            key={`mod-${id}-${i}`}
            points={pts(corners)}
            fill={off ? "none" : "#1e3a8a"}
            fillOpacity={off ? 0 : 1}
            stroke={off ? "#cbd5e1" : "#60a5fa"}
            strokeWidth={off ? stroke * 0.75 : stroke}
            strokeDasharray={off ? `${stroke * 3} ${stroke * 2}` : undefined}
            onClick={interactive ? () => onToggleModule!(id) : undefined}
            style={interactive ? { cursor: "pointer" } : undefined}
          >
            {interactive && <title>{off ? "Entferntes Modul — klicken zum Wiederherstellen" : "Modul — klicken zum Entfernen"}</title>}
          </polygon>
        );
      })}

      {/* Sperrflächen (No-Go-Zonen) — schraffiert, bernstein; Module wurden hier ausgeschlossen */}
      {keepoutsT.map((corners, i) => (
        <polygon
          key={`ko-${i}`}
          points={pts(corners)}
          fill="url(#keepout-hatch)"
          stroke="#b45309"
          strokeWidth={stroke * 1.5}
          strokeDasharray={`${stroke * 4} ${stroke * 2}`}
          strokeLinejoin="round"
        />
      ))}

      {/* Störflächen (Hindernisse) — über den Modulen, klar markiert */}
      {obstaclesT.map((corners, i) => (
        <polygon
          key={`obs-${i}`}
          points={pts(corners)}
          fill="#374151"
          fillOpacity={0.85}
          stroke="#f9fafb"
          strokeWidth={stroke * 1.5}
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}
